import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Code, 
  Download, 
  Star, 
  Eye, 
  GitBranch,
  FileCode
} from 'lucide-react';
import SEO from '../components/SEO';

const Scripts: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', label: 'Tümü' },
    { id: 'framework', label: 'Framework' },
    { id: 'jobs', label: 'Meslekler' },
    { id: 'vehicles', label: 'Araçlar' },
    { id: 'ui', label: 'Arayüz' },
    { id: 'utilities', label: 'Yardımcı' }
  ];

  const scripts = [
    {
      id: 1,
      name: 'ESX Framework',
      category: 'framework',
      description: 'FiveM için gelişmiş ESX framework sistemi',
      downloads: 45600,
      rating: 4.9,
      size: '15.2 MB',
      image: 'https://via.placeholder.com/400x250/1e293b/0ea5e9?text=ESX+Framework',
      features: ['Player Management', 'Database Integration', 'Job System', 'Inventory', 'Economy'],
      author: 'ESX Team',
      version: '1.9.4',
      status: 'stable',
      language: 'Lua'
    },
    {
      id: 2,
      name: 'QB-Core',
      category: 'framework',
      description: 'QB-Core framework sistemi ile gelişmiş oyun deneyimi',
      downloads: 38900,
      rating: 4.8,
      size: '12.8 MB',
      image: 'https://via.placeholder.com/400x250/1e293b/10b981?text=QB-Core',
      features: ['Core System', 'Player Data', 'Job Management', 'Housing', 'Banking'],
      author: 'QB Team',
      version: '2.1.3',
      status: 'stable',
      language: 'Lua'
    },
    {
      id: 3,
      name: 'Advanced Job System',
      category: 'jobs',
      description: 'Gelişmiş meslek sistemi ile özelleştirilebilir işler',
      downloads: 23400,
      rating: 4.7,
      size: '8.5 MB',
      image: 'https://via.placeholder.com/400x250/1e293b/ef4444?text=Job+System',
      features: ['Custom Jobs', 'Salary System', 'Promotions', 'Work Locations', 'Skills'],
      author: 'JobDev',
      version: '1.6.2',
      status: 'stable',
      language: 'Lua'
    },
    {
      id: 4,
      name: 'Vehicle Spawner',
      category: 'vehicles',
      description: 'Gelişmiş araç spawn sistemi ve yönetimi',
      downloads: 18700,
      rating: 4.6,
      size: '5.2 MB',
      image: 'https://via.placeholder.com/400x250/1e293b/f59e0b?text=Vehicle+Spawner',
      features: ['Vehicle Catalog', 'Spawn System', 'Customization', 'Permissions', 'Favorites'],
      author: 'VehicleDev',
      version: '2.0.1',
      status: 'stable',
      language: 'Lua'
    },
    {
      id: 5,
      name: 'Modern UI Framework',
      category: 'ui',
      description: 'Modern ve responsive UI framework sistemi',
      downloads: 15600,
      rating: 4.8,
      size: '3.8 MB',
      image: 'https://via.placeholder.com/400x250/1e293b/8b5cf6?text=UI+Framework',
      features: ['Responsive Design', 'Custom Themes', 'Animations', 'Components', 'Mobile Support'],
      author: 'UIDev',
      version: '1.4.5',
      status: 'stable',
      language: 'JavaScript'
    },
    {
      id: 6,
      name: 'Anti-Cheat System',
      category: 'utilities',
      description: 'Gelişmiş anti-cheat sistemi ile güvenli oyun ortamı',
      downloads: 29800,
      rating: 4.9,
      size: '6.7 MB',
      image: 'https://via.placeholder.com/400x250/1e293b/22c55e?text=Anti-Cheat',
      features: ['Detection System', 'Auto Ban', 'Logging', 'Whitelist', 'Performance Monitor'],
      author: 'SecurityDev',
      version: '3.2.0',
      status: 'stable',
      language: 'Lua'
    }
  ];

  const filteredScripts = selectedCategory === 'all' 
    ? scripts 
    : scripts.filter(script => script.category === selectedCategory);

  return (
    <>
      <SEO 
        title="Scriptler - FiveM Scriptleri | Aedon Community" 
        description="Gelişmiş ve optimize edilmiş script koleksiyonumuzu keşfedin. FiveM için gelişmiş framework, meslek sistemi, araç spawner ve daha fazlası." 
        keywords="FiveM, Scriptler, Framework, Meslekler, Araçlar, Arayüz, Yardımcı, ESX Framework, QB-Core, FiveM Script, Aedon Community" 
        url="/scripts"
      />
      <div className="min-h-screen pt-20 bg-dark-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-gaming font-bold text-white mb-4">
            Scriptler
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Gelişmiş ve optimize edilmiş script koleksiyonumuzu keşfedin
          </p>
        </motion.div>

        {/* Categories */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-wrap justify-center gap-4 mb-12"
        >
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                selectedCategory === category.id
                  ? 'bg-primary-600 text-white shadow-lg'
                  : 'bg-dark-700 text-gray-300 hover:bg-dark-600 hover:text-white'
              }`}
            >
              {category.label}
            </button>
          ))}
        </motion.div>

        {/* Scripts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {filteredScripts.map((script, index) => (
            <motion.div
              key={script.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="card hover:bg-dark-700 transition-all duration-300 overflow-hidden"
            >
              {/* Script Image */}
              <div className="relative h-48 mb-6">
                <img
                  src={script.image}
                  alt={script.name}
                  className="w-full h-full object-cover rounded-lg"
                />
                <div className="absolute top-4 right-4 flex items-center space-x-2">
                  <div className="flex items-center text-yellow-400 bg-dark-800/80 px-2 py-1 rounded">
                    <Star className="w-4 h-4 fill-current" />
                    <span className="ml-1 text-sm font-medium">{script.rating}</span>
                  </div>
                  <div className={`px-2 py-1 rounded text-xs font-medium ${
                    script.status === 'stable' 
                      ? 'bg-green-500/80 text-white' 
                      : 'bg-yellow-500/80 text-white'
                  }`}>
                    {script.status === 'stable' ? 'Stabil' : 'Beta'}
                  </div>
                </div>
              </div>

              {/* Script Info */}
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-white mb-2">{script.name}</h3>
                <p className="text-gray-400 text-sm mb-4">{script.description}</p>
                
                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center space-x-2">
                    <Download className="w-4 h-4 text-primary-400" />
                    <span className="text-sm text-gray-300">{script.downloads.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <FileCode className="w-4 h-4 text-green-400" />
                    <span className="text-sm text-gray-300">{script.size}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <GitBranch className="w-4 h-4 text-blue-400" />
                    <span className="text-sm text-gray-300">v{script.version}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Code className="w-4 h-4 text-purple-400" />
                    <span className="text-sm text-gray-300">{script.language}</span>
                  </div>
                </div>

                {/* Features */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-white mb-3">Özellikler:</h4>
                  <div className="flex flex-wrap gap-2">
                    {script.features.map((feature, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-dark-600 text-gray-300 text-xs rounded-full"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Author and Actions */}
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-400">
                    Geliştirici: <span className="text-primary-400">{script.author}</span>
                  </div>
                  <div className="flex space-x-2">
                    <button className="btn-secondary text-sm px-4 py-2">
                      <Eye className="w-4 h-4 mr-2" />
                      Detaylar
                    </button>
                    <button className="btn-primary text-sm px-4 py-2">
                      <Download className="w-4 h-4 mr-2" />
                      İndir
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Empty State */}
        {filteredScripts.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <Code className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-400 mb-2">
              Bu kategoride script bulunamadı
            </h3>
            <p className="text-gray-500">
              Farklı bir kategori seçmeyi deneyin
            </p>
          </motion.div>
        )}
      </div>
    </div>
    </>
  );
};

export default Scripts; 